import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { ImageMasterService } from 'src/app/ERP/Products/image-master/image-master.service';
import {
  ImageMasterCreateRequestDTO,
  ImageMasterDeleteRequestDTO,
  ImageMasterReadByMasterIdRequestDTO,
  ImageMasterUpdateRequestDTO
} from 'src/app/ERP/Products/image-master/image-master.interface';
import { DocMasterService } from 'src/app/Common/DocMasterService/docMaster.service';
import {
  DocMasterCreateRequestDTO,
  DocMasterDeleteRequestDTO,
  DocMasterReadByMasterIdRequestDTO,
  DocMasterUpdateRequestDTO
} from 'src/app/Common/DocMasterService/docMaster.interface';
import { MasterAttachment } from './master-attachment.interface';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { ValueListItemCreateRequestDTO } from 'src/app/Common/ValueListService/ValueListItem.interface';
import { ensureNumber } from 'src/app/Shared/customFunctions/CustomFunctions';

// Camera captures and image-format files (jpg/png/etc.) are stored in
// ImageMaster (ERP/IMG); everything else (pdf/excel/word/...) goes to
// DocMaster (AM/DCM) - same split used by the Marvel "Img Upload" custom
// page. Centralised here so every off-canvas-driven master form (Supplier,
// Vendor, Customer, Agency, ...) routes attachments the same way instead of
// each one reimplementing (and forgetting) the split.
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'];

// One flat, admin-managed ValueList of Document Name suggestions, shared by
// every consumer that offers the field (the off-canvas upload list, the
// company-detail Documents tab's rename modal, ...) instead of each one
// keeping its own copy of this lookup.
const DOCUMENT_LABEL_VL_CODE = 'DOCLABEL';

@Injectable({
  providedIn: 'root'
})
export class MasterAttachmentService {
  private imageMasterService = inject(ImageMasterService);
  private docMasterService = inject(DocMasterService);
  private valueListService = inject(ValueListService);

  isImageUrl(url: string): boolean {
    const ext = (url.split('.').pop() || '').toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
  }

  // Reads attachments already saved against a master record from BOTH
  // ImageMaster and DocMaster and merges them into one list.
  //
  // Each read is caught independently before the forkJoin: without this, a
  // single failing call (e.g. one endpoint not deployed yet, or a transient
  // error) would fail the WHOLE combined result and hide BOTH lists, even
  // the one that succeeded (forkJoin behaves like Promise.all - one
  // rejection kills everything). Same reasoning as the Marvel "Img Upload"
  // custom page, which uses Promise.allSettled for exactly this reason.
  //
  // Also tolerates a bare array response (not wrapped in {items: [...]})
  // in case a deployed environment's ReadByMasterId hasn't caught up to the
  // current {items} contract yet.
  readAttachments(
    masterType: string,
    masterId: number,
    tenantId: number,
    tenantBranchId: number
  ): Observable<MasterAttachment[]> {
    masterId = ensureNumber(masterId);
    tenantId = ensureNumber(tenantId);
    tenantBranchId = ensureNumber(tenantBranchId);
    if (!masterId) return of([]);

    const imgReq: ImageMasterReadByMasterIdRequestDTO = { masterType, masterId, tenantId, tenantBranchId };
    const docReq: DocMasterReadByMasterIdRequestDTO = { masterType, masterId, tenantId, tenantBranchId };

    return forkJoin({
      img: this.imageMasterService.ImageMaster_ReadByMasterId(imgReq).pipe(
        catchError(err => {
          console.error('Failed to read ImageMaster attachments', err);
          return of(null);
        })
      ),
      doc: this.docMasterService.DocMaster_ReadByMasterId(docReq).pipe(
        catchError(err => {
          console.error('Failed to read DocMaster attachments', err);
          return of(null);
        })
      )
    }).pipe(
      map(({ img, doc }) => {
        const imgRaw: any = img;
        const docRaw: any = doc;
        const imgList = Array.isArray(imgRaw) ? imgRaw : (imgRaw?.items || []);
        const docList = Array.isArray(docRaw) ? docRaw : (docRaw?.items || []);

        const imgItems: MasterAttachment[] = imgList.map((i: any) => ({
          source: 'IMG' as const,
          id: i.imgId,
          url: i.imgUrl,
          name: i.imgName,
          label: i.imgTitle,
          raw: i
        }));
        const docItems: MasterAttachment[] = docList.map((d: any) => ({
          source: 'DCM' as const,
          id: d.docId,
          url: d.docURL || '',
          name: d.docName,
          label: d.docDesc,
          raw: d
        }));
        return [...imgItems, ...docItems];
      })
    );
  }

  // Persists the diff between what the off-canvas upload modal ended up with
  // (pendingUrls) and what was already saved (existingAttachments). New urls
  // become ImageMaster_Create (image files) or DocMaster_Create (everything
  // else) rows depending on extension; urls the user removed become a
  // delete against whichever table they were originally saved in.
  saveAttachmentChanges(
    masterType: string,
    masterId: number,
    pendingUrls: string[],
    existingAttachments: MasterAttachment[],
    tenantId: number,
    tenantBranchId: number,
    actionUser: string,
    // Optional user-supplied label per url (e.g. "GST Certificate"), only
    // populated when the caller enabled <app-off-canvas enableAttachmentLabel>.
    // Stored as ImageMaster.imgTitle / DocMaster.docDesc - never required.
    labelsByUrl: { [url: string]: string } = {}
  ): Observable<any[]> {
    masterId = ensureNumber(masterId);
    tenantId = ensureNumber(tenantId);
    tenantBranchId = ensureNumber(tenantBranchId);

    const newUrls = (pendingUrls || []).filter(
      url => !existingAttachments.some(d => d.url === url)
    );
    const removedDocs = (existingAttachments || []).filter(
      d => !pendingUrls.includes(d.url)
    );

    if (newUrls.length === 0 && removedDocs.length === 0) {
      return of([]);
    }

    const createRequests = newUrls.map(url => {
      const fileName = url.split('/').pop() || '';
      const label = labelsByUrl[url] || '';

      if (this.isImageUrl(url)) {
        const payload: ImageMasterCreateRequestDTO = {
          masterType,
          masterId,
          tenantId,
          tenantBranchId,
          imgUrl: url,
          imgName: fileName,
          imgTitle: label,
          isDefault: 0,
          otherDetail1: '',
          otherDetail2: '',
          otherDetail3: '',
          otherDetail4: '',
          otherDetail5: '',
          otherDetail6: '',
          actionUser
        };
        return this.imageMasterService.ImageMaster_Create(payload);
      }

      const ext = (fileName.split('.').pop() || '').toUpperCase();
      const payload: DocMasterCreateRequestDTO = {
        masterType,
        masterId,
        tenantId,
        tenantBranchId,
        docURL: url,
        docName: fileName,
        docDesc: label,
        docType: ext || 'FILE',
        isDefault: 0,
        actionUser
      };
      return this.docMasterService.DocMaster_Create(payload);
    });

    const deleteRequests = removedDocs.map(doc => {
      if (doc.source === 'IMG') {
        const payload: ImageMasterDeleteRequestDTO = {
          imgId: doc.id,
          tenantId,
          tenantBranchId,
          actionUser
        };
        return this.imageMasterService.ImageMaster_Delete(payload);
      }

      const payload: DocMasterDeleteRequestDTO = {
        docId: doc.id,
        tenantId,
        tenantBranchId,
        actionUser
      };
      return this.docMasterService.DocMaster_Delete(payload);
    });

    return forkJoin([...createRequests, ...deleteRequests]);
  }

  // Renames the Document Name (ImageMaster.imgTitle / DocMaster.docDesc) on
  // an already-saved attachment. Rebuilds the update payload from the raw
  // row captured by readAttachments() so every other column round-trips
  // unchanged - Update is a full-replace call on both tables, so leaving a
  // field out or defaulting it wrong would silently wipe it.
  updateAttachmentLabel(
    attachment: MasterAttachment,
    newLabel: string,
    tenantId: number,
    tenantBranchId: number,
    actionUser: string
  ): Observable<any> {
    tenantId = ensureNumber(tenantId);
    tenantBranchId = ensureNumber(tenantBranchId);
    const raw = attachment.raw || {};

    if (attachment.source === 'IMG') {
      const payload: ImageMasterUpdateRequestDTO = {
        imgId: attachment.id,
        masterType: raw.masterType,
        masterId: raw.masterId,
        tenantId,
        tenantBranchId,
        imgName: raw.imgName || '',
        imgUrl: attachment.url,
        imgTitle: newLabel,
        isDefault: raw.isDefault ?? 0,
        otherDetail1: raw.otherDetail1 || '',
        otherDetail2: raw.otherDetail2 || '',
        otherDetail3: raw.otherDetail3 || '',
        otherDetail4: raw.otherDetail4 || '',
        otherDetail5: raw.otherDetail5 || '',
        otherDetail6: raw.otherDetail6 || '',
        isActive: raw.isActive ?? 1,
        actionUser
      };
      return this.imageMasterService.ImageMaster_Update(payload);
    }

    const payload: DocMasterUpdateRequestDTO = {
      docId: attachment.id,
      masterType: raw.masterType,
      masterId: raw.masterId,
      tenantId,
      tenantBranchId,
      docName: raw.docName || '',
      docDesc: newLabel,
      issuedDate: raw.issuedDate,
      expiryDate: raw.expiryDate,
      docType: raw.docType || '',
      docURL: attachment.url,
      isDefault: raw.isDefault ?? 0,
      otherDetail1: raw.otherDetail1 || '',
      otherDetail2: raw.otherDetail2 || '',
      otherDetail3: raw.otherDetail3 || '',
      otherDetail4: raw.otherDetail4 || '',
      otherDetail5: raw.otherDetail5 || '',
      otherDetail6: raw.otherDetail6 || '',
      isActive: raw.isActive ?? 1,
      actionUser
    };
    return this.docMasterService.DocMaster_Update(payload);
  }

  // Deletes a single already-saved attachment from whichever table it
  // actually lives in (ImageMaster vs. DocMaster, per attachment.source) -
  // same per-table dispatch saveAttachmentChanges already does for its own
  // removedDocs diff, just callable directly for a single row instead of
  // requiring a full pendingUrls diff. Used by pages that show a persistent
  // documents grid (e.g. company-detail's Documents tab) rather than only
  // ever deleting through the off-canvas upload modal's own existing-file
  // list.
  deleteAttachment(
    attachment: MasterAttachment,
    tenantId: number,
    tenantBranchId: number,
    actionUser: string
  ): Observable<void> {
    tenantId = ensureNumber(tenantId);
    tenantBranchId = ensureNumber(tenantBranchId);

    if (attachment.source === 'IMG') {
      const payload: ImageMasterDeleteRequestDTO = {
        imgId: attachment.id,
        tenantId,
        tenantBranchId,
        actionUser
      };
      return this.imageMasterService.ImageMaster_Delete(payload);
    }

    const payload: DocMasterDeleteRequestDTO = {
      docId: attachment.id,
      tenantId,
      tenantBranchId,
      actionUser
    };
    return this.docMasterService.DocMaster_Delete(payload);
  }

  // Suggestions for any "Document Name" field ('DOCLABEL' ValueList) -
  // admin just adds/renames VliName rows there, no per-consumer setup.
  //
  // Reads through ValueListService's own cached ValueList$/ValueListItems$
  // (via getItemByVLCode) instead of a one-shot ValueListItem_readByVlCode
  // HTTP call - the same source the UIC form engine's own
  // ValuelistSelectorComponent uses for its value-list dropdowns. This is
  // what makes a newly-added suggestion (see addDocumentNameSuggestion
  // below, which creates through the same ValueListItem_create) show up
  // immediately: that create pushes into ValueListItems$, and since this is
  // a live subscription (not a single completed HTTP call), it re-emits
  // automatically - callers must tear it down (e.g. takeUntil(destroy$))
  // since it never completes on its own.
  //
  // Returns an empty list (never a hardcoded fallback) if the ValueList
  // hasn't been configured for this tenant yet - the caller's field just
  // behaves as a plain text input in that case.
  // tenantId/tenantBranchId are unused here (getItemByVLCode scopes to the
  // current session's own cached tenant data) - kept in the signature only
  // so this reads consistently with every other method on this service.
  getDocumentNameSuggestions(tenantId: number, tenantBranchId: number): Observable<string[]> {
    return this.valueListService.getItemByVLCode(DOCUMENT_LABEL_VL_CODE).pipe(
      map(({ valueListItems }) => (valueListItems || [])
        .slice()
        .sort((a, b) => (a.displaySeq ?? 0) - (b.displaySeq ?? 0))
        .map(item => item.vliName)
        .filter(name => !!name)
      )
    );
  }

  // Called when the user types a custom Document Name that isn't already a
  // suggestion (the ng-select "Add item" option) - persists it as a new
  // ValueListItem under 'DOCLABEL' for this tenant/branch, so it shows up as
  // a real suggestion for everyone next time, instead of being a one-off
  // value that's forgotten as soon as this upload is done.
  //
  // Uses ValueListService's own cached ValueList$/ValueListItems$ (populated
  // app-wide by Home.component's FetchAllValueLists()) to resolve DOCLABEL's
  // valueListId and check for an existing case-insensitive match, rather
  // than a fresh HTTP call - same lookup address-detail's own value-list
  // dropdown already relies on.
  addDocumentNameSuggestion(
    name: string,
    tenantId: number,
    tenantBranchId: number,
    actionUser: string
  ): Observable<string> {
    const trimmed = (name || '').trim();
    if (!trimmed) return of(trimmed);

    tenantId = ensureNumber(tenantId);
    tenantBranchId = ensureNumber(tenantBranchId);

    return this.valueListService.getItemByVLCode(DOCUMENT_LABEL_VL_CODE).pipe(
      take(1),
      switchMap(({ ValueList, valueListItems }) => {
        const alreadyExists = valueListItems.some(
          item => (item.vliName || '').trim().toLowerCase() === trimmed.toLowerCase()
        );
        if (alreadyExists) return of(trimmed);

        if (!ValueList) {
          console.error(
            `'${DOCUMENT_LABEL_VL_CODE}' ValueList is not configured for this tenant - "${trimmed}" won't be saved as a reusable suggestion, just used for this upload.`
          );
          return of(trimmed);
        }

        const nextDisplaySeq = valueListItems.length
          ? Math.max(...valueListItems.map(item => item.displaySeq ?? 0)) + 1
          : 1;

        const payload: ValueListItemCreateRequestDTO = {
          valueListId: ValueList.valueListId,
          tenantId,
          tenantBranchId,
          vliCode: trimmed,
          vliName: trimmed,
          vliDesc: '',
          displaySeq: nextDisplaySeq,
          addField1: '',
          addField2: '',
          addField3: '',
          actionUser
        };
        return this.valueListService.ValueListItem_create(payload).pipe(
          map(() => trimmed),
          catchError(err => {
            console.error('Failed to save new document name suggestion', err);
            return of(trimmed);
          })
        );
      })
    );
  }
}
